import { useEffect, useState } from 'react';
import { Rating } from 'react-simple-star-rating';
import { useRouter } from 'next/router';
import Col from 'antd/lib/col';
import Row from 'antd/lib/row';
import classNames from 'classnames';

import { ButtonPrimary } from 'src/components/Button';
import Checkbox from 'src/components/Checkbox';
import Skeleton from 'src/components/Skeleton';
import StarRate from 'src/components/StarRate';
import {
	useGetGameReviewSummary,
	useInfiniteGetGameReviewList,
	usePostGameReview,
} from 'src/hooks';
import useAuthReducer from 'src/hooks/reducers/useAuthReducer';
import useAuthAction from 'src/hooks/useAuthAction';
import { IGameReview, IGameReviewSummary } from 'src/interface';
import useDeviceSize from 'src/utils/hooks/useDeviceSize';
import { notify } from 'src/utils/notify';

import IconStarEmpty from './IconStarEmpty';
import IconStarFill from './IconStarFill';
import ReviewList from './List';

import classes from './index.module.less';

interface Props {
	gameSlug: string;
}

const Review = (props: Props) => {
	const { gameSlug } = props;

	const deviceSize = useDeviceSize();

	const router = useRouter();
	const { slug } = router.query;

	const { user } = useAuthReducer();
	const { callAuthAction } = useAuthAction();

	const maxReviewLen = 100000;

	const [onEdit, setOnEdit] = useState<boolean>(true);
	const [inputRating, setInputRating] = useState<number>(5);
	const [inputReview, setInputReview] = useState<string>('');
	const [hideUsername, setHideUsername] = useState<boolean>(false);

	const { mutate: postReview, isLoading: isLoadingPostReview } = usePostGameReview();

	const {
		data: dataRS,
		isLoading: isLoadingReviewSummary,
		refetch: refetchReviewSummary,
	} = useGetGameReviewSummary(gameSlug);
	const summary: IGameReviewSummary = dataRS?.data?.data;

	const dataReviewList = useInfiniteGetGameReviewList({ slug: gameSlug, limit: 5 });
	const { refetch: refetchReviewList } = dataReviewList;

	const ownReview: IGameReview = (dataReviewList?.data?.pages?.[0] as any)?.data?.data?.mine;

	const isSubmitBtnDisabled = inputRating === 0 || inputReview.length < 3;

	const handleSubmitReviewClick = () => {
		postReview(
			{
				slug: slug as string,
				star: inputRating,
				comment: inputReview,
				hideUsername: hideUsername,
			},
			{
				onSuccess: (res: any) => {
					if (res.status === 200) {
						refetchReviewSummary();
						refetchReviewList();

						notify(
							// eslint-disable-next-line max-len
							' Your review has been successfully posted! Thank you for sharing your thoughts and insights about the game.',
						);

						if (!ownReview) {
							setInputRating(5);
							setInputReview('');
						} else {
							setOnEdit(false);
						}
					} else {
						notify(res?.summary?.message ?? 'Failed to post review', 'error');
					}
				},
			},
		);
	};

	useEffect(() => {
		// refetchReviewSummary();
		// refetchReviewList();
	}, [user?.api_token]);

	useEffect(() => {
		if (ownReview) {
			setOnEdit(false);
			setInputRating(ownReview.star);
			setInputReview(ownReview.comment);
			setHideUsername(ownReview.is_username_hidden);
		}
	}, [JSON.stringify(ownReview)]);

	if (isLoadingReviewSummary) {
		return (
			<div className={classes.review}>
				<Row gutter={20}>
					<Col xs={24} md={12}>
						<Skeleton className={classes['card-info']} borderRadius="6px" />
					</Col>
					<Col xs={24} md={12}>
						<Skeleton className={classes['card-info']} borderRadius="6px" />
					</Col>
				</Row>
			</div>
		);
	}
	return (
		<div className={classes.review}>
			<Row gutter={30}>
				<Col xs={24} md={12}>
					<div className="d-flex flex-column" style={{ gap: 20 }}>
						<div className={classNames(classes['card-info'])}>
							<StarRate
								ratingNumber={parseFloat(summary?.average_star ?? 0)}
								className={classNames(classes.content, classes['final-rating'])}
								type="large"
							/>
						</div>

						{deviceSize === 'mobile' && <ReviewList data={dataReviewList} />}

						<>
							{user?.api_token ? (
								<div className={classNames(classes.content, classes['write-review'])}>
									<Row gutter={[20, 10]} justify="space-between" align="middle">
										<Col>
											<span className={classes.title}>Your Review</span>
										</Col>
										{ownReview && !onEdit && (
											<Col>
												<a className="fs-20 fw-500" onClick={() => setOnEdit(true)}>
													Edit
												</a>
											</Col>
										)}
									</Row>
									<div className="d-flex flex-column">
										<div className={classes.label}>
											{ownReview && !onEdit ? 'You rate this game' : 'Rate this game'}
										</div>
										<Rating
											fillIcon={<IconStarFill />}
											emptyIcon={<IconStarEmpty />}
											initialValue={inputRating}
											onClick={(val) => setInputRating(val)}
											readonly={onEdit ? false : true}
										/>
										<div className={classNames('d-flex justify-content-between', classes.label)}>
											<span className="mr-2">Review</span>
											{onEdit && (
												<span>
													{inputReview.length}/{maxReviewLen} Character
												</span>
											)}
										</div>
										<textarea
											className={classes['input-review']}
											placeholder="Type your review here ..."
											value={inputReview}
											onChange={(e) => {
												if (e.target.value.length > maxReviewLen) return;
												setInputReview(e.target.value);
											}}
											disabled={!onEdit ? true : false}
										/>
									</div>
									{onEdit && (
										<Row gutter={30}>
											<Col xs={24} lg={12} className="d-flex align-items-center">
												<Checkbox
													checked={hideUsername}
													onChange={(e) => setHideUsername(e.target.checked)}
													label={<span className="fs-16 fw-500">Don’t show my username</span>}
												/>
											</Col>
											<Col xs={24} lg={12} className="d-flex justify-content-end">
												<ButtonPrimary
													className={classes.button}
													loading={isLoadingPostReview}
													disabled={isSubmitBtnDisabled}
													onClick={handleSubmitReviewClick}
												>
													Submit
												</ButtonPrimary>
											</Col>
										</Row>
									)}
								</div>
							) : (
								<div className={classNames(classes['card-info'])}>
									<div className={classNames(classes.content, classes['login-to-review'])}>
										<span>Login to write your review</span>
										<ButtonPrimary className={classes.button} onClick={() => callAuthAction()}>
											Login
										</ButtonPrimary>
									</div>
								</div>
							)}
						</>
					</div>
				</Col>
				{deviceSize !== 'mobile' && (
					<Col xs={24} md={12}>
						<ReviewList data={dataReviewList} />
					</Col>
				)}
			</Row>
		</div>
	);
};

export default Review;
